import { createContext, useContext, useState } from 'react'
import type { CSSProperties, ComponentType, ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Brand } from './ui'
import {
  IconGetReady, IconOrders, IconTickets, IconProfile, IconHelp, IconBell, IconPerson,
} from '../lib/icons'
import { HelpPanel, type HelpSection } from './HelpPanel'

/*
 * Shell chrome — transcribed from the App-shell canon §3.1 (desktop sidebar +
 * topbar, BASELINE) and §3.2 (mobile topbar + 5-tab bar, BASELINE).
 * DOM ids per canon §1.3: shell.root / shell.nav / shell.topbar /
 * shell.content / shell.help. testids per §3.1/§3.2 tables.
 */

/** Responsive breakpoint — canon §2.3 PROPOSED (§12.3): mobile < 768px. */
export const MOBILE_BREAKPOINT = 768

/** Canon §2.4 shell section names (`passes` is the internal name of Tickets). */
export type Section = 'ready' | 'orders' | 'passes' | 'profile'

/** Route map per canon §1.2 (PROPOSED, §12.1). Help is NEVER a route (§1.2). */
const SECTION_PATH: Record<Section, string> = {
  ready: '/get-ready',
  orders: '/orders',
  passes: '/tickets',
  profile: '/profile',
}

export function sectionForPath(pathname: string): Section {
  if (pathname === '/orders' || pathname.startsWith('/orders/')) return 'orders'
  if (pathname === '/tickets' || pathname.startsWith('/tickets/')) return 'passes'
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return 'profile'
  return 'ready'
}

/** Viewport seam for the content screens: page h1s do NOT render on mobile
 *  (`showPageHead = !isMobile`, canon §3.2). Screens read this via
 *  useShellChrome(); default (no shell) = desktop. */
const ShellChromeContext = createContext<{ isMobile: boolean; showPageHead: boolean }>({
  isMobile: false,
  showPageHead: true,
})
export const useShellChrome = () => useContext(ShellChromeContext)

interface NavDef { section: Section; label: string; testid: string; Icon: ComponentType<{ size?: number }> }
// Verbatim [chrome] nav labels (canon §3.1/§3.2) — exactly four sections, in
// this order; no fifth nav SECTION and no Help item in the desktop sidebar (§11.3).
const NAV: NavDef[] = [
  { section: 'ready', label: 'Get ready', testid: 'nav-getready', Icon: IconGetReady },
  { section: 'orders', label: 'Orders', testid: 'nav-orders', Icon: IconOrders },
  { section: 'passes', label: 'Tickets', testid: 'nav-tickets', Icon: IconTickets },
  { section: 'profile', label: 'Profile', testid: 'nav-profile', Icon: IconProfile },
]

// Verbatim [chrome] tooltips (canon §3.1/§3.2).
const TIP_HELP = 'Help and contact support'
const TIP_BELL = 'Notifications — heat allocated, ticket issued, event updates'

// ---------- brand row ----------
// Canon §3.1/§3.2 shows mark + wordmark; canon §11.1 mandates "the official
// FULL Trumin logo asset from the central Trumin asset store" — that asset is
// the vendored <trumin-logo> lockup (badge + wordmark in one component, no
// mark-only mode), so the row renders the official lockup, never the
// prototype's three.js/tru-badge copy and never a monogram tile (§11.2).
// The wordmark "Trumin" is part of the official asset; aria-label carries it.
function BrandRow({ markSize, testid }: { markSize: number; testid: string }) {
  return (
    <div data-testid={testid} aria-label="Trumin" style={{ display: 'flex', alignItems: 'center' }}>
      <Brand size={markSize} />
    </div>
  )
}

// ---------- topbar icon button (44px, radius 11 — canon §3.1) ----------
function topbarBtnStyle(active?: boolean): CSSProperties {
  return {
    flex: 'none',
    minWidth: 'var(--tap-min)',
    minHeight: 'var(--tap-min)',
    borderRadius: 11,
    border: `1px solid ${active ? 'rgba(63,224,200,.4)' : 'var(--hairline-10)'}`,
    background: active ? 'var(--tint-teal)' : 'var(--surface)',
    color: active ? 'var(--accent-bright)' : 'var(--text-secondary)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
  }
}

// Nav-badge tone → token pair. `warn` = amber Get-ready `now`-steps count
// (canon §3.1); `success` = the green ISSUED-tickets count on the Tickets item
// (BOARDROOM-2026-08-14-100859 UX #4). The green token has no bespoke ink, so it
// borrows --accent-ink (the same dark ink the teal accent uses) for contrast.
type BadgeTone = 'warn' | 'success'
function badgeColors(tone: BadgeTone): { background: string; color: string } {
  return tone === 'success'
    ? { background: 'var(--success)', color: 'var(--accent-ink)' }
    : { background: 'var(--warn)', color: 'var(--warn-ink)' }
}

// ---------- desktop sidebar nav item (canon §3.1 states) ----------
function DesktopNavItem({ def, active, badge, badgeTestid, badgeTone = 'warn', onClick }: {
  def: NavDef; active: boolean; badge?: number; badgeTestid?: string; badgeTone?: BadgeTone; onClick: () => void
}) {
  return (
    <button
      data-testid={def.testid}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 'var(--tap-min)',
        border: 0, borderRadius: 11, padding: '11px 13px', fontFamily: 'inherit',
        fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left', position: 'relative',
        color: active ? 'var(--accent-bright)' : 'var(--text-secondary)',
        background: active ? 'var(--tint-teal)' : 'transparent',
      }}
    >
      <def.Icon size={20} />
      <span>{def.label}</span>
      {/* Badge shown only when count > 0 (canon §3.1). */}
      {badge ? (
        <span
          data-testid={badgeTestid}
          style={{
            marginLeft: 'auto', minWidth: 21, height: 21, borderRadius: 20,
            ...badgeColors(badgeTone), fontSize: 11, fontWeight: 800,
            display: 'grid', placeItems: 'center', padding: '0 6px',
          }}
        >{badge}</span>
      ) : null}
    </button>
  )
}

// ---------- mobile tab (canon §3.2 states; help tab reflects PANEL state) ----------
function MobileTab({ label, testid, Icon, active, badge, badgeTestid, badgeTone = 'warn', onClick }: {
  label: string; testid: string; Icon: ComponentType<{ size?: number }>
  active: boolean; badge?: number; badgeTestid?: string; badgeTone?: BadgeTone; onClick: () => void
}) {
  return (
    <button
      data-testid={testid}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 3, flex: '1 1 0', minWidth: 0, border: 0, background: 'transparent',
        fontFamily: 'inherit', fontSize: 9.5, fontWeight: 700, cursor: 'pointer',
        padding: '6px 1px', position: 'relative', whiteSpace: 'nowrap',
        color: active ? 'var(--accent-bright)' : 'var(--text-faint)',
      }}
    >
      <Icon size={20} />
      <span>{label}</span>
      {badge ? (
        <span
          data-testid={badgeTestid}
          style={{
            position: 'absolute', top: 3, left: 'calc(50% + 7px)', minWidth: 17, height: 17,
            borderRadius: 20, ...badgeColors(badgeTone),
            fontSize: 9.5, fontWeight: 800, display: 'grid', placeItems: 'center', padding: '0 4px',
          }}
        >{badge}</span>
      ) : null}
    </button>
  )
}

// ---------- shell ----------
export function AppShell({ mobile, badgeCount, ticketsBadgeCount = 0, accountName, children }: {
  /** Viewport (caller computes via MOBILE_BREAKPOINT; canon §2.3). */
  mobile: boolean
  /** Get-ready nav badge = count of `now` steps across all events, derived per
   *  canon §2.4 — never a stored flag. 0 hides the badge. */
  badgeCount: number
  /** Tickets nav badge = number of ISSUED tickets in the wallet, derived per
   *  canon §2.4 (BOARDROOM-2026-08-14-100859 UX #4). 0 hides the badge. */
  ticketsBadgeCount?: number
  /** `{firstName} {lastName}` for the mobile account tooltip pattern
   *  `{firstName} {lastName} — your account` (canon §3.2; name is [demo] in
   *  the DC — production passes the real account holder). */
  accountName?: string
  children: ReactNode
}) {
  const [helpOpen, setHelpOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const section = sectionForPath(location.pathname)

  const goTo = (s: Section) => navigate(SECTION_PATH[s])

  // Per-section nav badge (DERIVED, canon §2.4 — never a stored flag): Get-ready
  // carries the amber `now`-steps count, Tickets the green ISSUED count; every
  // other section is unbadged. Testids stay per-section so tests target them
  // exactly (nav-*-badge desktop, tab-*-badge mobile).
  const sectionBadge = (s: Section): { count: number; tone: BadgeTone; desktopTestid: string; mobileTestid: string } | null => {
    if (s === 'ready' && badgeCount) {
      return { count: badgeCount, tone: 'warn', desktopTestid: 'nav-getready-badge', mobileTestid: 'tab-getready-badge' }
    }
    if (s === 'passes' && ticketsBadgeCount) {
      return { count: ticketsBadgeCount, tone: 'success', desktopTestid: 'nav-tickets-badge', mobileTestid: 'tab-tickets-badge' }
    }
    return null
  }

  const grid: CSSProperties = mobile
    ? {
        height: '100dvh', background: 'var(--bg)', position: 'relative', display: 'grid',
        gridTemplateColumns: '1fr', gridTemplateRows: 'auto 1fr auto',
        gridTemplateAreas: '"topbar" "content" "nav"', overflow: 'hidden',
      }
    : {
        height: '100dvh', background: 'var(--bg)', position: 'relative', display: 'grid',
        gridTemplateColumns: 'var(--sidebar-w) 1fr', gridTemplateRows: 'auto 1fr',
        gridTemplateAreas: '"nav topbar" "nav content"', overflow: 'hidden',
      }

  const navStyle: CSSProperties = mobile
    ? {
        gridArea: 'nav', display: 'flex', alignItems: 'stretch', background: 'var(--bg-frame)',
        borderTop: '1px solid var(--hairline-08)', padding: '6px 0',
      }
    : {
        gridArea: 'nav', display: 'flex', flexDirection: 'column', gap: 4,
        background: 'var(--bg-sidebar)', borderRight: '1px solid var(--hairline-07)',
        padding: '22px 16px',
      }

  const topbarStyle: CSSProperties = {
    gridArea: 'topbar', display: 'flex', alignItems: 'center', gap: mobile ? 6 : 10,
    padding: mobile ? '12px 10px' : '16px 24px',
    borderBottom: '1px solid var(--hairline-07)',
    background: mobile ? 'var(--bg)' : 'transparent',
  }

  const contentStyle: CSSProperties = {
    gridArea: 'content', overflow: 'auto',
    padding: mobile ? '18px 16px 26px' : '26px 30px 36px',
  }

  return (
    <ShellChromeContext.Provider value={{ isMobile: mobile, showPageHead: !mobile }}>
      <div id="shell.root" style={grid}>
        {/* NAV — sidebar (desktop) / bottom tab bar (mobile) */}
        <nav id="shell.nav" style={navStyle}>
          {mobile ? (
            <>
              {NAV.map(d => {
                const b = sectionBadge(d.section)
                return (
                  <MobileTab
                    key={d.section}
                    label={d.label}
                    testid={`tab-${d.testid.slice('nav-'.length)}`}
                    Icon={d.Icon}
                    active={section === d.section}
                    badge={b?.count}
                    badgeTestid={b?.mobileTestid}
                    badgeTone={b?.tone}
                    onClick={() => goTo(d.section)}
                  />
                )
              })}
              {/* Help is a tab on mobile but reflects PANEL state, not a
                  section — it toggles the sheet, no route change (canon §3.2). */}
              <MobileTab
                label="Help"
                testid="tab-help"
                Icon={IconHelp}
                active={helpOpen}
                onClick={() => setHelpOpen(o => !o)}
              />
            </>
          ) : (
            <>
              <div style={{ padding: '4px 8px 22px' }}>
                <BrandRow markSize={34} testid="nav-brand" />
              </div>
              {NAV.map(d => {
                const b = sectionBadge(d.section)
                return (
                  <DesktopNavItem
                    key={d.section}
                    def={d}
                    active={section === d.section}
                    badge={b?.count}
                    badgeTestid={b?.desktopTestid}
                    badgeTone={b?.tone}
                    onClick={() => goTo(d.section)}
                  />
                )
              })}
            </>
          )}
        </nav>

        {/* TOPBAR */}
        <div id="shell.topbar" style={topbarStyle}>
          {mobile && <BrandRow markSize={28} testid="topbar-brand" />}
          <div style={{ flex: 1 }} />
          <button data-testid="topbar-help" title={TIP_HELP} onClick={() => setHelpOpen(o => !o)} style={topbarBtnStyle(helpOpen)}>
            <IconHelp size={18} />
          </button>
          {/* Notifications: static — no wired behaviour in the DC (canon §3.1, open decision §12.4). */}
          <button data-testid="topbar-bell" title={TIP_BELL} style={topbarBtnStyle()}>
            <IconBell size={17} />
          </button>
          {mobile && (
            // Account control: line-art person icon (never initials, §11.2);
            // tapping opens Profile (canon §3.2).
            <button
              data-testid="topbar-account"
              title={accountName ? `${accountName} — your account` : 'Your account'}
              onClick={() => goTo('profile')}
              style={topbarBtnStyle()}
            >
              <IconPerson size={18} />
            </button>
          )}
        </div>

        {/* CONTENT */}
        <div id="shell.content" style={contentStyle}>{children}</div>

        {/* HELP — overlay/sheet toggled in place, NEVER a route (canon §1.2/§3.3). */}
        {helpOpen && (
          <div id="shell.help" style={{ display: 'contents' }}>
            <HelpPanel section={section as HelpSection} mobile={mobile} onClose={() => setHelpOpen(false)} />
          </div>
        )}
      </div>
    </ShellChromeContext.Provider>
  )
}
